/**
 * Requests where they live (migrations 959, 966, 974, 975, 1028, 1054; PRD-assignment-record.md,
 * PRD-tasks-asks-and-my-work.md FR-2). On a Registry commitment they are its Assignments; on an
 * item, a cycle, a track or a session they are its Requests. One panel for every home.
 *
 * Shows each assignment with the revision in force, the pending proposal if any, its
 * standing (derived by the view), plan coverage and the latest readback. Acts: Authorize
 * (new or amend), Accept, Withdraw, Readback (974), and the dispositions (975: suspend,
 * resume, release, fulfill, invalidate). A control is drawn only for a person who may
 * use it (build contract §2.8): authorize/amend/withdraw and the authority's
 * dispositions for holders of the plan-governance bar, accept for the person the
 * assignment binds, someone placed in its assignee unit ("Accept for the unit"), or a
 * workspace admin (690) — mayAcceptAssignment — readback for the bound party only,
 * fulfill also for the assignee of the revision in force. Responses (1028): the party
 * asked declines, counters, names a date to get back by, renegotiates a promise or
 * reports it done (availableResponses); the authority answers (mayAnswerResponse). See
 * AssignmentResponses.tsx. All of that is the Requests & Agreements module (1029): with it
 * off, Accept and the dispositions are what is drawn, and a response already open can
 * still be answered. With the Delegation module on as well (1036), a request names how
 * much is delegated (DelegationParts.tsx), carries it as a chip, and a promise in force
 * can be escalated.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, CalendarDays, Check, Circle, ShieldCheck, Clock, Undo2, MoreHorizontal, MessageSquareQuote } from 'lucide-react';
import { Modal, TextInput, TextArea, Select, Field, Checkbox } from '@/components/ds';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import {
  acceptAssignment,
  authorizeRequest,
  disposeAssignment,
  availableResponses,
  clearRequestParts,
  lastClosedResponseOf,
  listAssignmentResponses,
  listAssignmentsForHome,
  mayAnswerResponse,
  openResponseOf,
  openResponseWord,
  listAssignmentReadbacks,
  listReleasedTerms,
  mayReleaseInPart,
  readbackAssignment,
  responseHoldsRelease,
  releaseAssignmentInPart,
  assignmentStandingWord,
  assigneeLabel,
  answerDateHasPassed,
  availableDispositions,
  mayAcceptAssignment,
  holdsAssignmentAuthority,
  mayReadBack,
  planCoverageWord,
  withdrawAssignmentRevision,
  ASSIGNMENT_VERBS,
  DISPOSITION_WORDS,
  RESPONSE_WORDS,
  RELEASE_IN_PART_WORDS,
  type AssignmentReadback,
  type AuthorizeAssignmentInput,
  type AssignmentDispositionAct,
  type AssignmentGround,
  type AssignmentResponseAnswer,
  type AssignmentResponseKind,
  type AssignmentResponseRow,
  type AssignmentVerb,
  type AssignmentView,
  type RequestHome,
  REQUESTS_CHANGED_EVENT,
} from '@/lib/assignmentService';
import { AnswerModal, GroundsPicker, RequestInFull, RespondModal, ResponseReadout, WithdrawResponseModal } from './AssignmentResponses';
import { ClarifyTheWant, DelegationChip, DelegationFieldset } from './DelegationParts';
import { useRequestFormContext } from '@/hooks/useRequestFormContext';
import {
  delegationOfDraft, draftGaps, draftOf, listRevisionDelegations,
  type Delegation, type DelegationDraft, type DelegationLadder,
} from '@/lib/delegation';
import { NO_WANT, saveWant, type WantDraft } from '@/lib/clarifyTheWant';
import { readbackLine } from '../../../../supabase/functions/_shared/assignmentReadbackWords.ts';

interface Props {
  /** Where the requests live: a commitment, an item, a cycle, a track or a session. */
  home: RequestHome;
  workspaceId?: string | null;
  linkedItems: { id: string; title?: string | null }[];
  onChanged?: () => void;
  /**
   * The commitment was superseded (983): its assignments are history, read only. No act
   * is drawn; what became of each one is on its row.
   */
  readOnly?: boolean;
  /** Arrived from a link to this commitment (an ask): bring the assignments into view once they load. */
  scrollIntoViewOnLoad?: boolean;
}

const TONE: Record<'green' | 'mustard' | 'slate' | 'coral', string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)]',
  mustard: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  slate: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule)]',
  coral: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
};

function fmt(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A timestamp (not a date-only term) as a day. */
function fmtAt(ts: string | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const NO_UNITS: string[] = [];


export function AssignmentsSection({ home, workspaceId: wsProp, linkedItems, onChanged, readOnly = false, scrollIntoViewOnLoad = false }: Props) {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = wsProp ?? currentWorkspaceId ?? null;
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const mayAuthorize = !readOnly && holdsAssignmentAuthority(can);
  const isAdmin = !readOnly && can('admin.settings');
  const { requestsAndAgreements, delegationOn, ladder, users, people, units } = useRequestFormContext(workspaceId);
  const names = useMemo(() => ({
    users: Object.fromEntries(users.map((u) => [u.id, u.name || u.email])),
    units: Object.fromEntries(units.map((u) => [u.id, u.name])),
  }), [users, units]);

  const [rows, setRows] = useState<AssignmentView[] | null>(null);
  const [responses, setResponses] = useState<AssignmentResponseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ assignmentId?: string; inForce?: AssignmentView } | null>(null);
  const [withdrawing, setWithdrawing] = useState<AssignmentView | null>(null);
  const [disposing, setDisposing] = useState<{ row: AssignmentView; act: AssignmentDispositionAct } | null>(null);
  const [releasingPart, setReleasingPart] = useState<AssignmentView | null>(null);
  const [readbacks, setReadbacks] = useState<Map<string, AssignmentReadback>>(new Map());
  const [released, setReleased] = useState<Map<string, { text: string; at: string | null }[]>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [responding, setResponding] = useState<{ row: AssignmentView; kind: AssignmentResponseKind } | null>(null);
  const [answering, setAnswering] = useState<{ response: AssignmentResponseRow; answer: AssignmentResponseAnswer; unit: string | null } | null>(null);
  const [takingBack, setTakingBack] = useState<AssignmentResponseRow | null>(null);
  const [delegations, setDelegations] = useState<Map<string, Delegation>>(new Map());

  const reload = useCallback(async () => {
    try {
      const list = await listAssignmentsForHome(home);
      const ids = list.flatMap((r) => (r.assignment_id ? [r.assignment_id] : []));
      const [standing, parts, said, levels] = await Promise.all([
        listAssignmentReadbacks(ids),
        listReleasedTerms(ids),
        listAssignmentResponses(ids),
        listRevisionDelegations(list.flatMap((r) => [r.in_force_revision_id, r.proposed_revision_id].filter((x): x is string => !!x))),
      ]);
      setDelegations(levels);
      setRows(list);
      setReadbacks(new Map(standing.flatMap((r) => (r.assignment_id ? [[r.assignment_id, r] as const] : []))));
      setReleased(parts);
      setResponses(said);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [home.type, home.id, home.workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps -- the home's parts are its identity

  useEffect(() => { void reload(); }, [reload]);

  // A request made with Ask elsewhere on the page (1054) shows up here at once.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ type: string; id: string | null }>).detail;
      if (d && d.type === home.type && d.id === home.id) void reload();
    };
    window.addEventListener(REQUESTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(REQUESTS_CHANGED_EVENT, onChanged);
  }, [home.type, home.id, reload]);

  const sectionRef = useRef<HTMLElement>(null);
  const hasRows = !!rows;
  useEffect(() => {
    if (scrollIntoViewOnLoad && hasRows) sectionRef.current?.scrollIntoView({ block: 'start' });
  }, [scrollIntoViewOnLoad, hasRows]);

  // A readback on an org-unit assignment is given by someone placed in that unit. The
  // placements come from the org chart the item page also loads; read only when needed.
  const hasUnitAssignee = !!rows?.some((r) => r.assignee_org_unit_id);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const placements = useEnterpriseIdentityStore((s) => s.userOrgUnitIdsByUser);
  useEffect(() => {
    if (hasUnitAssignee && workspaceId) void fetchOrgUnits({ workspaceId });
  }, [hasUnitAssignee, workspaceId, fetchOrgUnits]);
  const myUnitIds = (userId ? placements.get(userId) : undefined) ?? NO_UNITS;

  const groundsFromPage: AssignmentGround[] = useMemo(() => [
    ...linkedItems.map((i) => ({ kind: 'item' as const, id: i.id, label: i.title ?? '(untitled)' })),
    ...(rows ?? []).filter((r) => r.assignment_id).map((r) => ({
      kind: 'assignment' as const,
      id: r.assignment_id as string,
      label: `${assigneeLabel(r, names)}: ${r.statement ?? linkedItems.find((i) => i.id === r.item_id)?.title ?? 'an item'}`,
    })),
  ], [linkedItems, rows, names]);
  const personName = (id: string | null | undefined) => (id ? names.users[id] ?? null : null);

  const onCommitment = home.type === 'commitment';
  const binding = rows?.filter((r) => r.is_binding).length ?? 0;
  const proposed = rows?.filter((r) => r.proposed_revision_id).length ?? 0;

  async function accept(r: AssignmentView) {
    if (!r.proposed_revision_id) return;
    setBusy(r.assignment_id ?? '');
    const res = await acceptAssignment(r.proposed_revision_id, null);
    setBusy(null);
    if (!res.ok) { setError(res.error); return; }
    await reload(); onChanged?.();
  }

  return (
    <section ref={sectionRef} className="scroll-mt-16">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
          {onCommitment ? 'Assignments' : 'Requests'}
          {rows && rows.length > 0 && (
            <span className="ml-1.5 font-normal normal-case tracking-normal">
              ({rows.length} · {binding} binding{proposed ? ` · ${proposed} proposed` : ''})
            </span>
          )}
        </div>
        {/* On any other home, Ask in the page header makes the request (one act, one place). */}
        {mayAuthorize && onCommitment && (
          <button
            type="button"
            onClick={() => setForm({})}
            className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <Plus className="h-3 w-3" /> Authorize an assignment
          </button>
        )}
      </div>

      {error && <div className="text-xs text-[var(--coral)] mb-2">{error}</div>}

      {rows && rows.length === 0 && (
        <p className="text-xs text-[var(--text-ter)] italic">
          {onCommitment
            ? 'No assignment yet. A commitment binds nobody until an authority names who will do what, and they take it on.'
            : 'Nobody has been asked to do anything here yet.'}
        </p>
      )}

      <div className="space-y-1.5">
        {rows?.map((r) => {
          const word = assignmentStandingWord(r);
          const coverage = planCoverageWord(r);
          const iAmAssignee = !!r.assignee_user_id && r.assignee_user_id === userId;
          const who = iAmAssignee ? 'You' : assigneeLabel(r, names);
          const subject = r.item_id
            ? (linkedItems.find((i) => i.id === r.item_id)?.title ?? 'an item')
            : (r.statement ?? '');
          const open = openResponseOf(responses, r.assignment_id);
          const releasesPart = !readOnly && mayReleaseInPart(r, { mayAuthorize }) && !responseHoldsRelease(open);
          const lastAnswer = open ? null : lastClosedResponseOf(responses, r.assignment_id);
          // A counteroffer on it: the ball is with the person who asked, and it is taken back before a yes.
          const acceptAs = readOnly || open?.response === 'counteroffer' ? null : mayAcceptAssignment(r, { userId, orgUnitIds: myUnitIds, isAdmin });
          const mayAccept = acceptAs !== null;
          const responseActs = readOnly || !requestsAndAgreements ? [] : availableResponses(r, open, { userId, orgUnitIds: myUnitIds }, { delegation: delegationOn });
          const inForceLevel = r.in_force_revision_id ? delegations.get(r.in_force_revision_id) ?? null : null;
          const proposedLevel = r.proposed_revision_id ? delegations.get(r.proposed_revision_id) ?? null : null;
          const shownLevel = proposedLevel ?? inForceLevel;
          const askAgain = mayAuthorize && !!open && open.response !== 'commit_to_commit';
          const pendingNeedsOther = !readOnly && !!r.proposed_revision_id && !mayAccept && !r.assignee_external;
          const dispositions = readOnly ? [] : availableDispositions(r, { mayAuthorize, userId, askerConfirmsDone: requestsAndAgreements });
          const readsBack = !readOnly && mayReadBack(r, { userId, orgUnitIds: myUnitIds });
          const standingOfReadback = r.assignment_id ? readbacks.get(r.assignment_id) : undefined;
          const openReadback = readbackLine(standingOfReadback?.readback_state, standingOfReadback?.unresolved_reason);
          return (
            <div key={r.assignment_id ?? ''} className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--text-pri)]">
                    <span className="font-medium">{who}</span>
                    <span className="text-[var(--text-sec)]"> {r.verb ? `will ${r.verb}` : 'will'} </span>
                    {r.item_id ? (
                      <Link to={`/prioritization/items/${r.item_id}`} className="font-medium hover:underline">{subject}</Link>
                    ) : (
                      <span>{subject}</span>
                    )}
                    {r.counterparty && <span className="text-[var(--text-ter)]"> · for {r.counterparty}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    {open?.response === 'counteroffer' || (open?.response === 'decline' && !r.in_force_revision_id) ? null : (
                      <span className={`px-1.5 py-0.5 border rounded-full font-medium ${TONE[word.tone]}`}>{word.label}</span>
                    )}
                    {open && (
                      <span className={`px-1.5 py-0.5 border rounded-full font-medium ${TONE[openResponseWord(open).tone]}`}>{openResponseWord(open).label}</span>
                    )}
                    {r.due_on ? (
                      <span className={`inline-flex items-center gap-0.5 ${r.is_overdue ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                        <CalendarDays className="h-3 w-3" /> by {fmt(r.due_on)}{r.is_overdue ? ' · overdue' : ''}
                      </span>
                    ) : r.proposed_due_on ? (
                      <span className="inline-flex items-center gap-0.5 text-[var(--text-ter)]">
                        <CalendarDays className="h-3 w-3" /> by {fmt(r.proposed_due_on)} (proposed)
                      </span>
                    ) : null}
                    {r.quantity != null && <span className="text-[var(--text-ter)]">{String(r.quantity)} {r.unit ?? ''}</span>}
                    {coverage && (
                      <span className={r.plan_coverage ? 'text-[var(--text-ter)]' : 'text-[var(--mustard)]'}>{coverage}</span>
                    )}
                    {r.in_force_revision_no != null && Number(r.revision_count ?? 1) > 1 && (
                      <span className="text-[var(--text-ter)]">rev {r.in_force_revision_no} of {String(r.revision_count)}</span>
                    )}
                  </div>
                  {shownLevel && (
                    <DelegationChip
                      delegation={shownLevel}
                      ladder={ladder}
                      proposed={!!proposedLevel && proposedLevel.level !== inForceLevel?.level && !!r.in_force_revision_id}
                      outcome={subject}
                      doneMeans={r.proposed_revision_id ? (r.proposed_acceptance_standard ?? r.acceptance_standard) : r.acceptance_standard}
                    />
                  )}
                  {r.proposed_revision_id ? (
                    <div className="mt-1"><RequestInFull row={r} requesterName={personName(r.proposed_by)} showDue={false} /></div>
                  ) : (
                    <>
                      {r.scope_statement && (
                        <div className="mt-1 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Scope:</span> {r.scope_statement}</div>
                      )}
                      {r.acceptance_standard && (
                        <div className="mt-1 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Met when:</span> {r.acceptance_standard}</div>
                      )}
                      {r.success_standard && (
                        <div className="mt-0.5 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Success would be:</span> {r.success_standard}</div>
                      )}
                    </>
                  )}
                  {Number(r.open_signal_count ?? 0) > 0 && r.item_id && (
                    <Link to={`/prioritization/items/${r.item_id}`} className="mt-1 block text-xs text-[var(--mustard)] hover:underline">
                      {String(r.open_signal_count)} open {Number(r.open_signal_count) === 1 ? 'signal' : 'signals'} on this item
                    </Link>
                  )}
                  {r.proposed_revision_id && r.in_force_revision_id && (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--mustard)]">
                      <Clock className="h-3 w-3" />
                      Amendment proposed{r.proposed_due_on ? ` (by ${fmt(r.proposed_due_on)})` : ''}; {fmt(r.due_on) ? `${fmt(r.due_on)} stays in force` : 'the current terms stay in force'} until the assignee accepts.
                    </div>
                  )}
                  {pendingNeedsOther && !open && (
                    <div className="mt-1 text-xs text-[var(--text-ter)]">
                      Waiting for {who} to accept{r.assignee_org_unit_id ? ' (anyone in that unit, or a workspace admin)' : ''}.
                    </div>
                  )}
                  {r.proposed_revision_id && !open && answerDateHasPassed(r.respond_by) && (
                    <div className="mt-1 text-xs text-[var(--coral)]" data-testid="answer-date-passed">
                      The date for an answer has passed.
                    </div>
                  )}
                  {r.binding_basis === 'hierarchy' && (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
                      <ShieldCheck className="h-3 w-3" /> Bound by the authority above; not accepted by {who}.
                    </div>
                  )}
                  {r.latest_readback_text && (
                    <div className="mt-1 flex items-start gap-1 text-xs text-[var(--text-sec)]">
                      <MessageSquareQuote className="h-3 w-3 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
                      <span>
                        <span className="text-[var(--text-ter)]">Readback{r.latest_readback_at ? ` · ${fmtAt(r.latest_readback_at)}` : ''}:</span>{' '}
                        “{r.latest_readback_text}”
                      </span>
                    </div>
                  )}
                  {openReadback && (
                    <div className="mt-1 flex items-start gap-1 text-xs text-[var(--mustard)]" data-testid="readback-unresolved">
                      <MessageSquareQuote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{openReadback}</span>
                    </div>
                  )}
                  {(r.assignment_id ? released.get(r.assignment_id) ?? [] : []).map((part) => (
                    <div key={`${part.at ?? ''}${part.text}`} className="mt-1 text-xs text-[var(--text-sec)]" data-testid="released-in-part">
                      <span className="text-[var(--text-ter)]">Released{part.at ? ` ${fmtAt(part.at)}` : ''}:</span> {part.text}
                    </div>
                  ))}
                  {readsBack && r.assignment_id && (
                    <ReadbackBox
                      assignmentId={r.assignment_id}
                      hasReadback={!!r.latest_readback_text}
                      onDone={async () => { await reload(); onChanged?.(); }}
                    />
                  )}
                  {(open ?? lastAnswer) && (
                    <ResponseReadout
                      response={(open ?? lastAnswer) as AssignmentResponseRow}
                      whoSaid={(open ?? lastAnswer)?.said_by === userId ? 'You' : personName((open ?? lastAnswer)?.said_by) ?? who}
                      iSaidIt={(open ?? lastAnswer)?.said_by === userId}
                      mayAnswer={!readOnly && !!open && mayAnswerResponse(open, { mayAuthorize, userId })}
                      mayAskAgain={askAgain}
                      onAnswer={(answer) => open && setAnswering({ response: open, answer, unit: r.unit ?? null })}
                      onAskAgain={() => setForm({ assignmentId: r.assignment_id ?? undefined, inForce: r })}
                      onWithdraw={() => open && setTakingBack(open)}
                      unitNow={r.unit}
                      ladder={ladder}
                    />
                  )}
                  {responseActs.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4">
                      {responseActs.map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          onClick={() => setResponding({ row: r, kind })}
                          className="min-h-[44px] md:min-h-0 text-xs text-[var(--text-sec)] underline underline-offset-4 hover:text-[var(--text-pri)]"
                        >
                          {RESPONSE_WORDS[kind].act}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {mayAccept && (
                    <button
                      type="button"
                      disabled={busy === r.assignment_id}
                      onClick={() => void accept(r)}
                      className="text-xs px-2 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
                    >
                      {acceptAs === 'assignee' ? 'Accept' : acceptAs === 'assignee_unit' ? 'Accept for the unit' : 'Accept for them'}
                    </button>
                  )}
                  {mayAuthorize && r.proposed_revision_id && (
                    <button
                      type="button"
                      onClick={() => setWithdrawing(r)}
                      className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--coral)]"
                    >
                      <Undo2 className="h-3 w-3" /> Withdraw
                    </button>
                  )}
                  {mayAuthorize && r.in_force_revision_id && !r.proposed_revision_id && !askAgain && (r.disposition === 'open' || r.disposition === 'suspended') && (
                    <button
                      type="button"
                      onClick={() => setForm({ assignmentId: r.assignment_id ?? undefined, inForce: r })}
                      className="text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)]"
                    >
                      Amend
                    </button>
                  )}
                  {dispositions.length > 0 && (
                    <DispositionMenu
                      acts={dispositions}
                      onPick={(act) => setDisposing({ row: r, act })}
                      onReleasePart={releasesPart ? () => setReleasingPart(r) : undefined}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <AuthorizeAssignmentModal
          home={home}
          assignmentId={form.assignmentId}
          inForce={form.inForce}
          linkedItems={linkedItems}
          users={people}
          units={units}
          workspaceId={workspaceId}
          extraGrounds={groundsFromPage.filter((g) => g.id !== form.assignmentId)}
          clearRequest={requestsAndAgreements}
          delegationOn={delegationOn}
          ladder={ladder}
          amendedDelegation={form.inForce
            ? delegations.get(form.inForce.proposed_revision_id ?? '') ?? delegations.get(form.inForce.in_force_revision_id ?? '') ?? null
            : null}
          onClose={() => setForm(null)}
          onDone={async () => { setForm(null); await reload(); onChanged?.(); }}
        />
      )}

      {withdrawing && (
        <WithdrawModal
          row={withdrawing}
          onClose={() => setWithdrawing(null)}
          onDone={async () => { setWithdrawing(null); await reload(); onChanged?.(); }}
        />
      )}

      {responding && (
        <RespondModal
          row={responding.row}
          kind={responding.kind}
          subject={`${responding.row.verb ?? 'do'} ${responding.row.item_id
            ? (linkedItems.find((i) => i.id === responding.row.item_id)?.title ?? 'an item')
            : (responding.row.statement ?? '')}`}
          requesterName={personName(responding.row.proposed_by)}
          workspaceId={workspaceId}
          extraGrounds={groundsFromPage.filter((g) => g.id !== responding.row.assignment_id)}
          delegation={(responding.kind === 'counteroffer' || responding.kind === 'decline' || responding.kind === 'commit_to_commit'
            ? delegations.get(responding.row.proposed_revision_id ?? '')
            : delegations.get(responding.row.in_force_revision_id ?? '')) ?? null}
          delegationOn={delegationOn}
          ladder={ladder}
          onClose={() => setResponding(null)}
          onDone={async () => { setResponding(null); await reload(); onChanged?.(); }}
        />
      )}

      {answering && (
        <AnswerModal
          response={answering.response}
          answer={answering.answer}
          unitNow={answering.unit}
          ladder={ladder}
          onClose={() => setAnswering(null)}
          onDone={async () => { setAnswering(null); await reload(); onChanged?.(); }}
        />
      )}

      {takingBack && (
        <WithdrawResponseModal
          response={takingBack}
          onClose={() => setTakingBack(null)}
          onDone={async () => { setTakingBack(null); await reload(); onChanged?.(); }}
        />
      )}

      {disposing && (
        <DispositionModal
          row={disposing.row}
          act={disposing.act}
          onClose={() => setDisposing(null)}
          onDone={async () => { setDisposing(null); await reload(); onChanged?.(); }}
        />
      )}

      {releasingPart && (
        <ReleaseInPartModal
          row={releasingPart}
          onClose={() => setReleasingPart(null)}
          onDone={async () => { setReleasingPart(null); await reload(); onChanged?.(); }}
        />
      )}
    </section>
  );
}

// ── Readback (974) ────────────────────────────────────────────────────────────

function ReadbackBox({ assignmentId, hasReadback, onDone }: { assignmentId: string; hasReadback: boolean; onDone: () => void | Promise<void> }) {
  // With no readback yet the box is open; once there is one, it opens on request.
  const [again, setAgain] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true); setErr(null);
    const res = await readbackAssignment(assignmentId, text.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    setText(''); setAgain(false);
    await onDone();
  }

  if (hasReadback && !again) {
    return (
      <button
        type="button"
        onClick={() => setAgain(true)}
        className="mt-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)]"
      >
        Read back again
      </button>
    );
  }
  return (
    <div className="mt-2 border-t border-[var(--rule)] pt-2">
      <Field label="Readback" hint="What will you do differently? In your own words; it does not change whether the assignment binds.">
        <TextArea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="What will you do differently?" />
      </Field>
      <div className="mt-1.5 flex items-center justify-end gap-2">
        {hasReadback && (
          <button type="button" onClick={() => { setAgain(false); setErr(null); }} className="text-xs px-2 py-1 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
        )}
        <button
          type="button"
          disabled={!text.trim() || saving}
          onClick={() => void submit()}
          className="text-xs px-2 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
        >
          {saving ? 'Recording…' : 'Read back'}
        </button>
      </div>
      {err && <div className="text-xs text-[var(--coral)] mt-1">{err}</div>}
    </div>
  );
}

// ── Dispositions (975) ────────────────────────────────────────────────────────

function DispositionMenu({ acts, onPick, onReleasePart }: {
  acts: AssignmentDispositionAct[];
  onPick: (act: AssignmentDispositionAct) => void;
  /** Drawn only when this person may release part of this assignment now. */
  onReleasePart?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Change this assignment"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-pri)]"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 z-20 mt-1 w-44 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg py-1 text-xs">
            {acts.flatMap((act) => {
              const item = (
                <button
                  key={act}
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); onPick(act); }}
                  className={`w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] ${act === 'invalidate' ? 'text-[var(--coral)]' : 'text-[var(--text-sec)]'}`}
                >
                  {DISPOSITION_WORDS[act].act}
                </button>
              );
              if (act !== 'release' || !onReleasePart) return [item];
              return [item, (
                <button
                  key="release-in-part"
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); onReleasePart(); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)]"
                >
                  {RELEASE_IN_PART_WORDS.act}
                </button>
              )];
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ReleaseInPartModal({ row, onClose, onDone }: { row: AssignmentView; onClose: () => void; onDone: () => void | Promise<void> }) {
  const [releasedWords, setReleasedWords] = useState('');
  const [quantity, setQuantity] = useState('');
  const [scope, setScope] = useState('');
  const [standard, setStandard] = useState('');
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const hasQuantity = row.quantity != null;
  const saysWhatIsLeft = quantity.trim().length > 0 || scope.trim().length > 0;
  const ready = releasedWords.trim().length > 0 && basis.trim().length > 0 && saysWhatIsLeft;

  async function submit() {
    if (!row.assignment_id) return;
    setSaving(true); setErr(null);
    const res = await releaseAssignmentInPart(row.assignment_id, {
      released: releasedWords.trim(),
      basis: basis.trim(),
      quantity: quantity.trim() ? Number(quantity) : null,
      scopeStatement: scope.trim() || null,
      acceptanceStandard: standard.trim() || null,
    });
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={RELEASE_IN_PART_WORDS.title}
      description={RELEASE_IN_PART_WORDS.meaning}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
          <button
            type="button"
            disabled={!ready || saving}
            onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Recording…' : 'Release this part'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="What is released" required hint="The part the party no longer owes.">
          <TextArea rows={2} value={releasedWords} onChange={(e) => setReleasedWords(e.target.value)} placeholder="The last two sites" />
        </Field>
        {hasQuantity && (
          <Field label="Quantity left" hint={`${String(row.quantity)} ${row.unit ?? ''} is in force. Enter what is left, lower than that.`}>
            <TextInput type="number" min={0} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder={String(row.quantity)} />
          </Field>
        )}
        <Field label="Scope left" hint={row.scope_statement ? `In force: ${row.scope_statement}` : 'What the party still owes, in a sentence.'}>
          <TextArea rows={2} value={scope} onChange={(e) => setScope(e.target.value)} placeholder="What remains" />
        </Field>
        <Field label="Met when" hint="Change this only if what counts as met changes with it.">
          <TextInput value={standard} onChange={(e) => setStandard(e.target.value)} placeholder={row.acceptance_standard ?? 'Leave as it is'} />
        </Field>
        <Field label="Basis" required hint="Recorded with the decision this writes.">
          <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Why this part is discharged unmet" />
        </Field>
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}

function DispositionModal({ row, act, onClose, onDone }: { row: AssignmentView; act: AssignmentDispositionAct; onClose: () => void; onDone: () => void | Promise<void> }) {
  const words = DISPOSITION_WORDS[act];
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!row.assignment_id) return;
    setSaving(true); setErr(null);
    const res = await disposeAssignment(row.assignment_id, act, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={words.title}
      description={words.meaning}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)]">Cancel</button>
          <button type="button" disabled={!basis.trim() || saving} onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] disabled:opacity-40">
            {saving ? 'Recording…' : words.act}
          </button>
        </div>
      }
    >
      <Field label="Basis" required hint="Recorded with the decision this writes.">
        <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder={words.basisPrompt} />
      </Field>
      {words.ends && row.proposed_revision_id && (
        <p className="text-xs text-[var(--text-ter)] mt-2">The proposed revision still waiting is withdrawn with it.</p>
      )}
      {err && <div className="text-xs text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}

// ── Authorize / amend ─────────────────────────────────────────────────────────

type AssigneeKind = 'person' | 'unit' | 'external';

export function AuthorizeAssignmentModal({
  home, assignmentId, inForce, linkedItems, users, units, workspaceId, extraGrounds, clearRequest,
  delegationOn, ladder, amendedDelegation, onClose, onDone,
}: {
  home: RequestHome;
  assignmentId?: string;
  inForce?: AssignmentView;
  linkedItems: { id: string; title?: string | null }[];
  users: { id: string; name: string }[];
  units: { id: string; name: string }[];
  workspaceId: string | null;
  extraGrounds: AssignmentGround[];
  /** Requests & Agreements on: the form asks for the five parts of a clear request. */
  clearRequest: boolean;
  /** Delegation on (1036): the form asks how much is delegated. */
  delegationOn: boolean;
  ladder: DelegationLadder;
  /** On an amendment: the level of what it amends, to start from. */
  amendedDelegation: Delegation | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const amend = !!assignmentId;
  const [kind, setKind] = useState<AssigneeKind>(inForce?.assignee_external ? 'external' : inForce?.assignee_org_unit_id ? 'unit' : 'person');
  const [assigneeUserId, setAssigneeUserId] = useState(inForce?.assignee_user_id ?? '');
  const [assigneeUnitId, setAssigneeUnitId] = useState(inForce?.assignee_org_unit_id ?? '');
  const [assigneeExternal, setAssigneeExternal] = useState(inForce?.assignee_external ?? '');
  const [counterparty, setCounterparty] = useState(inForce?.counterparty ?? '');
  const [verb, setVerb] = useState<AssignmentVerb>((inForce?.verb as AssignmentVerb) ?? 'deliver');
  const [itemId, setItemId] = useState(inForce?.item_id ?? (linkedItems[0]?.id ?? ''));
  const [statement, setStatement] = useState(inForce?.statement ?? '');
  const [dueOn, setDueOn] = useState(inForce?.due_on ?? inForce?.proposed_due_on ?? '');
  const [quantity, setQuantity] = useState(inForce?.quantity != null ? String(inForce.quantity) : '');
  const [unit, setUnit] = useState(inForce?.unit ?? '');
  const [scope, setScope] = useState(inForce?.scope_statement ?? '');
  const [standard, setStandard] = useState(inForce?.acceptance_standard ?? '');
  const [success, setSuccess] = useState(inForce?.success_standard ?? '');
  const [respondBy, setRespondBy] = useState('');
  const [ifNoResponse, setIfNoResponse] = useState('');
  const [grounds, setGrounds] = useState<AssignmentGround[]>([]);
  const [evidence, setEvidence] = useState('');
  const [basis, setBasis] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [delegation, setDelegation] = useState<DelegationDraft>(() => draftOf(amendedDelegation));
  const [want, setWant] = useState<WantDraft>(NO_WANT);

  const hasTerm = !!(dueOn || quantity || scope.trim() || standard.trim());
  const hasSubject = !!(itemId || statement.trim());
  const hasAssignee = kind === 'person' ? !!assigneeUserId : kind === 'unit' ? !!assigneeUnitId : !!assigneeExternal.trim();
  // An outside party's promise is recorded, not asked for: the five parts are for a request.
  const parts = clearRequest && kind !== 'external'
    ? clearRequestParts({ hasListener: hasAssignee, hasAction: hasSubject, sufficient: standard, dueOn })
    : null;
  // An outside party's promise is recorded, not delegated: it has no responses to escalate with.
  const delegates = delegationOn && kind !== 'external';
  const delegationGaps = delegates ? draftGaps(delegation, ladder) : [];
  const ready = hasTerm && hasSubject && hasAssignee && confirmed && (!amend || basis.trim().length > 0)
    && (!ifNoResponse.trim() || !!respondBy) && (!parts || parts.every((p) => p.ok)) && delegationGaps.length === 0;
  const subjectWords = statement.trim() || linkedItems.find((i) => i.id === itemId)?.title || '…';

  async function submit() {
    setSaving(true); setErr(null);
    const wanted = clearRequest && kind !== 'external' ? await saveWant(want, standard) : null;
    if (wanted?.error) { setSaving(false); setErr(wanted.error); return; }
    const input: AuthorizeAssignmentInput = {
      assignmentId,
      assigneeUserId: kind === 'person' ? assigneeUserId : null,
      assigneeOrgUnitId: kind === 'unit' ? assigneeUnitId : null,
      assigneeExternal: kind === 'external' ? assigneeExternal.trim() : null,
      counterparty: counterparty.trim() || null,
      verb,
      itemId: itemId || null,
      statement: statement.trim() || null,
      dueOn: dueOn || null,
      quantity: quantity ? Number(quantity) : null,
      unit: unit.trim() || null,
      scopeStatement: scope.trim() || null,
      acceptanceStandard: standard.trim() || null,
      successStandard: success.trim() || null,
      respondBy: respondBy || null,
      ifNoResponse: ifNoResponse.trim() || null,
      grounds: wanted?.ground ? [wanted.ground, ...grounds] : grounds,
      externalEvidenceRef: kind === 'external' ? (evidence.trim() || null) : null,
      basis: basis.trim() || null,
      determinateConfirmed: confirmed,
      delegation: delegates ? delegationOfDraft(delegation, ladder) : undefined,
    };
    const res = await authorizeRequest(home, input);
    setSaving(false);
    if (!res.ok) { await wanted?.discard(); setErr(res.error); return; }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={amend ? 'Amend this assignment' : home.type === 'commitment' ? 'Authorize an assignment' : 'Ask someone to do something'}
      description={amend
        ? 'A new revision is proposed. What is in force stays in force until the assignee accepts the change.'
        : home.type === 'commitment'
          ? 'Name who is bound, to do what, by when. It binds when they accept it, or at once when an outside promise carries evidence, or when the workspace lets authority above the assignee bind.'
          : 'Name who you are asking, what you need, how you will both know it is done, and by when. It is in their list until they answer: accept, decline, offer something else, or say when they will know.'}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
          <button
            type="button"
            disabled={!ready || saving}
            onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Recording…' : amend ? 'Propose the amendment' : home.type === 'commitment' ? 'Authorize' : 'Ask'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {clearRequest && kind !== 'external' && !amend && <ClarifyTheWant value={want} onChange={setWant} />}

        <Field label="Who is bound" required>
          <div className="flex gap-2 mb-2 text-xs">
            {(['person', 'unit', 'external'] as AssigneeKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`px-2 py-1 border ${kind === k ? 'border-[var(--text-pri)] text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-ter)]'}`}
              >
                {k === 'person' ? 'A person' : k === 'unit' ? 'An org unit' : 'An outside party'}
              </button>
            ))}
          </div>
          {kind === 'person' && (
            <Select value={assigneeUserId} onChange={(e) => setAssigneeUserId(e.target.value)} placeholder="Choose a person"
              options={users.map((u) => ({ value: u.id, label: u.name }))} />
          )}
          {kind === 'unit' && (
            <Select value={assigneeUnitId} onChange={(e) => setAssigneeUnitId(e.target.value)} placeholder="Choose a unit"
              options={units.map((u) => ({ value: u.id, label: u.name }))} />
          )}
          {kind === 'external' && (
            <div className="space-y-2">
              <TextInput value={assigneeExternal} onChange={(e) => setAssigneeExternal(e.target.value)} placeholder="The outside party that promised (or was promised)" />
              <TextInput value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Evidence: the contract clause, email or document that establishes it" />
              <p className="text-xs text-[var(--text-ter)]">Without evidence the record reads “asserted”, never binding.</p>
            </div>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Verb" required>
            <Select value={verb} onChange={(e) => setVerb(e.target.value as AssignmentVerb)} options={ASSIGNMENT_VERBS.map((v) => ({ value: v, label: v }))} />
          </Field>
          <Field label="For (counterparty)" hint="Who was promised, if anyone">
            <TextInput value={counterparty} onChange={(e) => setCounterparty(e.target.value)} />
          </Field>
        </div>

        <Field label="What it is about" required hint="A linked item, or a statement when there is none">
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)} placeholder="No item: use a statement"
            options={linkedItems.map((i) => ({ value: i.id, label: i.title ?? '(untitled)' }))} />
          <TextInput className="mt-2" value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="Statement (when there is no item, or to say more)" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Due on"><TextInput type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} /></Field>
          <Field label="Quantity"><TextInput type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
          <Field label="Unit"><TextInput value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="hours, seats, %" /></Field>
        </div>
        <Field label="Scope"><TextInput value={scope} onChange={(e) => setScope(e.target.value)} placeholder="What is in, what is out" /></Field>
        <Field label="Met when" hint="What is sufficient: how a named person will judge it met. Not the outcome the item points toward.">
          <TextArea rows={2} value={standard} onChange={(e) => setStandard(e.target.value)} />
        </Field>
        {clearRequest && (
          <Field label="Success would be" hint="Beyond what is sufficient: what you would be glad to get. It is never what “met” is judged against.">
            <TextArea rows={2} value={success} onChange={(e) => setSuccess(e.target.value)} />
          </Field>
        )}
        {!hasTerm && <p className="text-xs text-[var(--mustard)]">At least one term is needed: a date, a quantity, a scope or a standard. Intent lives on the item.</p>}

        {delegates && (
          <DelegationFieldset value={delegation} onChange={setDelegation} ladder={ladder} outcome={subjectWords} doneMeans={standard} />
        )}

        {amend ? (
          <Field label="Why this revision" required>
            <TextInput value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="The basis for the change" />
          </Field>
        ) : (
          <Field label="What got us here" hint="The context, so they can answer well.">
            <TextInput value={basis} onChange={(e) => setBasis(e.target.value)} />
          </Field>
        )}

        {clearRequest && kind !== 'external' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Answer needed by"><TextInput type="date" value={respondBy} onChange={(e) => setRespondBy(e.target.value)} /></Field>
            <div className="sm:col-span-2">
              <Field label="If no answer comes" hint="Tell them what happens then. If the date passes with no answer, you are asked what to do. Nothing happens by itself.">
                <TextInput value={ifNoResponse} onChange={(e) => setIfNoResponse(e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        {clearRequest && (
          <GroundsPicker workspaceId={workspaceId} value={grounds} onChange={setGrounds} extra={extraGrounds} signalItemId={itemId || null} />
        )}

        {parts && (
          <div data-testid="clear-request-check" className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
            <div className="text-xs font-medium text-[var(--text-pri)] mb-1">Is this a clear request?</div>
            <ul className="space-y-0.5">
              {parts.map((p) => (
                <li key={p.part} className={`flex items-start gap-1.5 text-xs ${p.ok ? 'text-[var(--text-sec)]' : 'text-[var(--mustard)]'}`}>
                  {p.ok ? <Check className="h-3 w-3 mt-0.5 flex-shrink-0" /> : <Circle className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                  <span><span className="font-medium">{p.part}:</span> {p.question}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {delegationGaps.length > 0 && (
          <p className="text-xs text-[var(--mustard)]" data-testid="delegation-gaps">Still to say about the delegation: {delegationGaps.join(', ').toLowerCase()}.</p>
        )}

        <Checkbox
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          label="As written, this assignment can later be judged met or not met."
        />
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}

function WithdrawModal({ row, onClose, onDone }: { row: AssignmentView; onClose: () => void; onDone: () => void | Promise<void> }) {
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!row.proposed_revision_id) return;
    setSaving(true);
    const res = await withdrawAssignmentRevision(row.proposed_revision_id, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Withdraw the proposed revision"
      description={row.in_force_revision_id ? 'The revision in force is untouched.' : 'Nothing is in force for this assignment; withdrawing leaves it with no binding revision.'}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)]">Cancel</button>
          <button type="button" disabled={!basis.trim() || saving} onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] disabled:opacity-40">Withdraw</button>
        </div>
      }
    >
      <Field label="Basis" required>
        <TextInput value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Why it is taken back" />
      </Field>
      {err && <div className="text-xs text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}
